import React, { useState, useEffect } from 'react';
import { useWebSocket, useUser } from './WebSocketContext';
import {
  Typography,
  Box,
  Tooltip,
  IconButton,
  Paper,
  Fade,
  Grow,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import { useTranslation } from 'react-i18next';
import { TransitionGroup } from 'react-transition-group';
import OutlookApiConnector from './SettingOutlookApiConnector';
import SettingFolders from './SettingFoldersConnector';
import GenericConnector from './GenericConnector';

const SettingDatasources = ({ caseid, hideHeader = false, autoOpenConnector, onConnectorOpened }) => {
  const { t } = useTranslation();
  const { isReadOnly } = useUser();
  const { serverInfo } = useWebSocket();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isVisible, setIsVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const isWindowsServer = serverInfo?.isWindows === true
    || serverInfo?.osType === 'win32'
    || serverInfo?.platform === 'Windows';

  // Trigger animation when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  if (isReadOnly) {
    return null;
  }

  // Define all connectors in an array for easier animation mapping
  const allConnectors = [
    ...(isWindowsServer ? [
      { Component: GenericConnector, props: { caseid, type: "outlooklocal", title: "Outlook", useImageIcon: true, sx: { color: '#5757b0' } }, title: "Outlook" },
    ] : []),
    { Component: OutlookApiConnector, props: { caseid }, title: "Outlook API" },
    { Component: SettingFolders, props: { caseid }, title: "Folders" },
    { Component: GenericConnector, props: { caseid, type: "API", title: "Generic API", useImageIcon: true }, title: "Generic API" },
    { Component: GenericConnector, props: { caseid, type: "Dropbox", title: "Dropbox", useImageIcon: true }, title: "Dropbox" },
    { Component: GenericConnector, props: { caseid, type: "Excel", title: "Excel", useImageIcon: true }, title: "Excel" },
    { Component: GenericConnector, props: { caseid, type: "CSV", title: "CSV", useImageIcon: true }, title: "CSV" },
    { Component: GenericConnector, props: { caseid, type: "Gmail", title: "Google Gmail", useImageIcon: true }, title: "Gmail" },
    { Component: GenericConnector, props: { caseid, type: "Gnews", title: "GNews.io", useImageIcon: true }, title: "GNews" },
    { Component: GenericConnector, props: { caseid, type: "MCP", title: "MCP Connector", useImageIcon: true }, title: "MCP" },
    { Component: GenericConnector, props: { caseid, type: "Mediastack", title: "Mediastack", useImageIcon: true }, title: "Mediastack" },
    { Component: GenericConnector, props: { caseid, type: "NewsAPIAI", title: "NewsAPI.ai", useImageIcon: true }, title: "NewsAPI.ai" },
    { Component: GenericConnector, props: { caseid, type: "NewsAPI", title: "NewsAPI.api", useImageIcon: true }, title: "NewsAPI" },
    { Component: GenericConnector, props: { caseid, type: "Salesforce", title: "Salesforce", useImageIcon: true }, title: "Salesforce" },
    { Component: GenericConnector, props: { caseid, type: "Slack", title: "Slack", useImageIcon: true }, title: "Slack" },
    { Component: GenericConnector, props: { caseid, type: "Whatsapp", title: "Whatsapp", useImageIcon: true }, title: "Whatsapp" },
    { Component: GenericConnector, props: { caseid, type: "Airtable", title: "Airtable", useImageIcon: true }, title: "Airtable" },
    { Component: GenericConnector, props: { caseid, type: "Notion", title: "Notion", useImageIcon: true }, title: "Notion" },
    { Component: GenericConnector, props: { caseid, type: "Test", title: "Test Connector", useImageIcon: true }, title: "Test" },
    { Component: GenericConnector, props: { caseid, type: "PythonScript", title: "Python Script", useImageIcon: true }, title: "Python Script" },
    { Component: GenericConnector, props: { caseid, type: "WebScraper", title: "Web Scraper", useImageIcon: true }, title: "Web Scraper" },
  ];

  // Filter connectors based on search term
  const filteredConnectors = allConnectors.filter(connector =>
    connector.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Clear search function
  const handleClearSearch = () => {
    setSearchTerm('');
  };
  return (
    <>
      {!hideHeader && (
        <Fade in={true} timeout={600}>
          <Box sx={{ mb: { xs: 1, sm: 2 } }}>
            <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
              {t("dataconnectors.title")}
              <Tooltip title={t("settings.manage_data_connectors")}>
                <IconButton size={isMobile ? "small" : "medium"}>
                  <InfoIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>
            </Typography>
          </Box>
        </Fade>
      )}

      {/* Search Input */}
      <Fade in={isVisible} timeout={600}>
        <Box sx={{
          mb: { xs: 2, sm: 3 },
          display: 'flex',
          justifyContent: 'center',
          width: '100%'
        }}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              maxWidth: { xs: 280, sm: 320 },
              width: '100%',
              borderRadius: '24px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
              border: '2px solid rgba(87, 87, 176, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'rgba(87, 87, 176, 0.3)',
                boxShadow: '0 4px 20px rgba(87, 87, 176, 0.15)',
              },
              '&:focus-within': {
                borderColor: '#5757b0',
                boxShadow: '0 0 0 4px rgba(87, 87, 176, 0.1)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            <SearchIcon sx={{ color: '#5757b0', ml: { xs: 1.5, sm: 2 }, fontSize: { xs: '1.1rem', sm: '1.3rem' } }} />
            <TextField
              fullWidth
              variant="standard"
              placeholder={t("search.placeholder", "Search connectors...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                mx: { xs: 0.5, sm: 1 },
                '& .MuiInputBase-root': {
                  padding: { xs: '4px 8px', sm: '8px 8px' },
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  fontWeight: '500',
                  color: '#333',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(87, 87, 176, 0.6)',
                  opacity: 1
                },
                '& .MuiInput-underline:before': { borderBottom: 'none' },
                '& .MuiInput-underline:after': { borderBottom: 'none' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
              }}
              InputProps={{
                disableUnderline: true,
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClearSearch}
                      edge="end"
                      size="small"
                      sx={{
                        mr: 1,
                        color: 'rgba(87, 87, 176, 0.6)',
                        '&:hover': {
                          color: '#5757b0',
                          backgroundColor: 'rgba(87, 87, 176, 0.08)'
                        }
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Paper>
        </Box>
      </Fade>

      <Fade in={isVisible} timeout={800}>
        <Paper elevation={0} sx={{ p: 2, backgroundColor: 'transparent' }}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="stretch"
            gap={0}
            flexWrap="wrap"
            sx={{
              width: '100%',
              minHeight: filteredConnectors.length > 0 ? 'auto' : '200px',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '& > *': {
                display: 'flex',
                alignItems: 'stretch',
              }
            }}
          >
            <TransitionGroup component={null}>
              {filteredConnectors.length === 0 && searchTerm ? (
                <Fade in={true} timeout={500}>
                  <Box
                    sx={{
                      width: '100%',
                      textAlign: 'center',
                      py: 4,
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body1">
                      {t("search.no_results", "No connectors found matching your search.")}
                    </Typography>
                  </Box>
                </Fade>
              ) : (
                filteredConnectors.map((connector, index) => {
                  const { Component, props, title } = connector;
                  // Add forceOpen prop if this connector should be auto-opened
                  const shouldAutoOpen = autoOpenConnector && title === autoOpenConnector;
                  const componentProps = shouldAutoOpen
                    ? { ...props, forceOpen: true, onOpen: onConnectorOpened }
                    : props;

                  return (
                    <Grow
                      key={`${props.type || Component.name}-${index}`}
                      in={isVisible}
                      timeout={{
                        enter: 500 + (index * 100), // Stagger animation
                        exit: 300,
                      }}
                      style={{
                        transformOrigin: '0 0',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          width: { xs: '90px', sm: '110px', md: '130px' },
                          minHeight: { xs: '100px', sm: '110px', md: '130px' },
                          animation: `slideInUp 0.7s ease-out ${index * 0.08}s both`,
                          '@keyframes slideInUp': {
                            '0%': {
                              opacity: 0,
                              transform: 'translateY(30px) scale(0.8)',
                            },
                            '60%': {
                              opacity: 0.8,
                              transform: 'translateY(-3px) scale(1.05)',
                            },
                            '100%': {
                              opacity: 1,
                              transform: 'translateY(0px) scale(1)',
                            },
                          },
                          '& > *': {
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }
                        }}
                      >
                        <Component {...componentProps} />
                      </Box>
                    </Grow>
                  );
                })
              )}
            </TransitionGroup>
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default SettingDatasources;
